/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { bindQuerySelector, el, hide, jsx, show } from '@amazon/vinyl-tsx'
import { getAuth, notSignedInOrPending } from '@/auth/authRef'
import { logDebug } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { AuthStatus } from '@/auth/auth'
import { getString } from '@/auth/i18n/bundle.generated'
import { qrCodeSvg } from '@/components/qrCode'

const { target, errorHandler } = ctx('CodeLinkCallout')

export function CodeLinkCallout() {
    return (
        <div
            id="codeLinkCallout"
            className="codeLinkCallout"
            onConnect={() => {
                const auth = getAuth()
                const signIn = () => {
                    // CBL Auth needs to call signIn to initialize the code.
                    displayPublicCodePrompt(null)
                    auth.signIn()
                }

                return auth.status.onData((value) => {
                    if (value === AuthStatus.SIGNED_OUT) {
                        setTimeout(signIn)
                    }
                })
            }}
            visible={notSignedInOrPending()}
        >
            <div
                className="logo"
                role="img"
                aria-label={getString('dmusic_public_code_prompt_logo_accessibility_label')}
            />

            <h1>{getString('dmusic_public_code_prompt_title')}</h1>
            <div className="columns">
                <section className="scanColumn">
                    <h2>{getString('dmusic_public_code_prompt_scan_title')}</h2>
                    <p>{getString('dmusic_public_code_prompt_scan_instructions')}</p>
                    <div className="qrCode"></div>
                </section>
                <section className="codeColumn">
                    <h2>{getString('dmusic_public_code_prompt_other_title')}</h2>
                    <p className="instructions">
                        {getString('dmusic_public_code_prompt_code_instructions_prefix')}{' '}
                        <a className="signInLink" target="_blank">
                            {getString('dmusic_public_code_prompt_code_url_link')}
                        </a>
                    </p>
                    <span className="codeLink" />
                </section>
            </div>
        </div>
    )
}

/**
 * Updates the codeLinkCallout div with the public code.
 *
 * @param publicCode
 */
export function displayPublicCodePrompt(publicCode: string | null): () => void {
    logDebug(target, 'displayPublicCodePrompt', publicCode)
    const codeLinkCallout = el('#codeLinkCallout')
    const viewEl = bindQuerySelector(codeLinkCallout)
    viewEl('.codeLink').textContent = publicCode ?? '...'

    // A null code is the pre-fetch placeholder, not a dismissal: the prompt must
    // stay up showing '...' or a code request that never completes leaves the
    // device on a blank screen. Dismissal is the returned disposer.
    show(codeLinkCallout)

    const signInLink = viewEl('.signInLink') as HTMLAnchorElement
    if (publicCode) {
        const url = `https://www.amazon.com/code?cbl-code=${encodeURIComponent(publicCode)}&cbl-status=redirect_get_request`
        signInLink.href = url

        qrCodeSvg(url)
            .then((svg) => {
                viewEl('.qrCode').innerHTML = svg
            })
            .catch(errorHandler)
    } else {
        // Drop any previous code's link and QR — scanning a stale code next to
        // a '...' placeholder would send the user to a dead pairing.
        signInLink.removeAttribute('href')
        viewEl('.qrCode').innerHTML = ''
    }
    return () => hide(codeLinkCallout)
}
